/**
 * The figures on the quotation's totals strip (Akhil, 2026-09-04).
 *
 * Every formula here is his, written down so it cannot drift:
 *
 *     Total Quantity   Σ qty
 *     Total PO Cost    Σ (po_currency_rate × qty)
 *     grand total      net_total + tax_amount, and sometimes + (net_total × tax_rate / 100)
 *
 * `total_po_cost`, `net_total`, `total`, `grand_total`, `customer_currency` and `po_currency` are
 * real fields on the live `Quotation`, so these compute what the server will store rather than
 * inventing a parallel set of numbers.
 */

import type { Currency, Money, QuotationLine } from './types'

const round2 = (n: number): number => Math.round(n * 100) / 100

/** Every line's quantity. Fractional quantities are real on a converted UOM and are not rounded. */
export function totalQuantity(lines: readonly QuotationLine[]): number {
  return round2(lines.reduce((sum, l) => sum + (Number.isFinite(l.qty) ? l.qty : 0), 0))
}

/**
 * What the goods cost US — `Σ (rate × qty)`, in the company's currency.
 *
 * Akhil's formula names `po_currency_rate`, which has no field on `Quotation Line` yet (`D22`), so
 * this uses `cost_unit` — the same figure, already on the line and already what the PO columns are
 * populated from.
 *
 * A line with no cost contributes NOTHING rather than NaN. One unpriced line must not make the
 * whole PO cost unreadable, and a zero cost means "we were never told", not "it was free" — the
 * reading `marginGate.ts` and `marginFloor.ts` both use.
 */
export function totalPoCost(lines: readonly QuotationLine[]): number {
  return round2(
    lines.reduce((sum, l) => {
      const cost = l.cost_unit.amount
      if (!Number.isFinite(cost) || cost <= 0 || !Number.isFinite(l.qty)) return sum
      return sum + cost * l.qty
    }, 0),
  )
}

/**
 * The PO cost in the COMPANY's currency, whatever the quotation is billed in (Akhil, 2026-09-16:
 * "a reference for the purchaser on how much money it is"). The lines' costs follow the
 * quotation's currency after a re-price; `exchangeRate` is the document's company → quotation
 * rate, so dividing by it takes them home. A rate of 1, or none, leaves the figure as it is.
 */
export function companyPoCost(lines: readonly QuotationLine[], exchangeRate: number): number {
  const inQuotation = totalPoCost(lines)
  const rate = Number.isFinite(exchangeRate) && exchangeRate > 0 ? exchangeRate : 1
  return round2(inQuotation / rate)
}

export interface TaxInput {
  /** A charge entered as a flat amount — the ERP's "Actual" row. */
  amount?: number
  /** A charge entered as a percentage — the ERP's "On Net Total" row. */
  ratePct?: number
}

/**
 * `net_total + tax`, where tax may be a flat amount, a percentage of the net, or both at once —
 * the ERP screen shows an "Actual" charge and an "On Net Total" charge on the same quotation.
 *
 * The percentage is always taken on the NET TOTAL, never on net-plus-the-actual-charge.
 * Compounding one charge onto another would quietly overcharge the customer, and no one reading
 * the total would be able to see where the difference came from.
 */
export function grandTotal(netTotal: number, tax: TaxInput): number {
  const flat = Number.isFinite(tax.amount) ? (tax.amount as number) : 0
  const pct = Number.isFinite(tax.ratePct) ? (tax.ratePct as number) : 0
  return round2(netTotal + flat + (netTotal * pct) / 100)
}

/**
 * A total restated in the customer's currency, or `null` when no rate is on file.
 *
 * `null` rather than a conversion at par: a second figure shown at 1:1 looks entirely correct and
 * is wrong, which is exactly the failure the comparison screen shipped with (`$50.00 = AED 50.00`).
 * Better to show one currency than two, one of which is a lie.
 */
export function convertTotal(amount: number, currency: Currency, rate: number | null): Money | null {
  if (rate === null || !Number.isFinite(rate) || rate <= 0) return null
  return { amount: round2(amount * rate), currency }
}

/**
 * The margin once the commission has been paid out of the price (Akhil, 2026-09-17).
 *
 * "Overall margin" is sell over cost and includes the commission grossed into the price
 * (`quotationCommission.ts`'s `applyMarkup`): 100 cost, 30% markup, 10% commission → 144.44,
 * overall 44.4%. The commission is `sell × pct` and leaves; what stays over cost is 30 — the
 * markup asked for. That is this figure.
 */
export function marginAfterCommission(input: { sell: number; cost: number; commissionPct: number }): {
  commission: number
  keptOverCost: number
  pct: number | null
} {
  const pct = Number.isFinite(input.commissionPct) && input.commissionPct > 0 ? input.commissionPct : 0
  const commission = round2((input.sell * pct) / 100)
  const keptOverCost = round2(input.sell - commission - input.cost)
  return { commission, keptOverCost, pct: input.cost > 0 ? round2((keptOverCost / input.cost) * 100) : null }
}
